import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminBrandsApi, adminCategoriesApi, adminProductsApi, adminAttributesApi } from '../api/catalog';
import { adminFilesApi } from '../api/sales';
import { adminTaxApi } from '../api/operations';
import type {
  ProductAttributeDto,
  ProductOptionUpsertDto,
  ProductStatus,
  ProductUpsertDto,
  ProductVariantUpsertDto,
} from '../api/types';
import { productKindValues, productStatusValues } from '../api/types';
import { PageHeader } from '../components/Shell';
import { Field, FormErrors, Modal, PageLoader, Spinner } from '../components/ui';
import { RichTextEditor } from '../components/RichTextEditor';
import { GoogleSearchPreview } from '../components/GoogleSearchPreview';
import { useToast } from '../components/Toast';
import { flattenCategories } from './ProductsPage';
import { productKindLabels, productStatusLabels } from '../lib/labels';
import { slugify } from '../lib/format';
import {
  Camera,
  ImagePlus,
  CheckCircle2,
  XCircle,
  Upload,
  Trash2,
  X,
  ImageIcon,
  Check,
  Palette,
  Plus,
  RefreshCw,
  AlertCircle,
  SlidersHorizontal,
} from 'lucide-react';

/* ------------------------------------------------------------------ *
 *  Urun olusturma / duzenleme.
 *
 *  Varyantlar seceneklerin kartezyen carpimindan uretilir. Mevcut
 *  varyantlarin fiyat ve stok esigi korunur; yalnizca yeni kombinasyonlar
 *  eklenir. Boylece bir eksene deger eklemek eski varyantlari silmez.
 * ------------------------------------------------------------------ */

const TABS = [
  { key: 'genel', label: 'Genel' },
  { key: 'varyantlar', label: 'Seçenek ve varyantlar' },
  { key: 'gorseller', label: 'Görseller' },
  { key: 'seo', label: 'SEO' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

function emptyProduct(): ProductUpsertDto {
  return {
    title: '',
    slug: '',
    shortDescription: '',
    description: '',
    status: 'Draft',
    kind: 'Physical',
    categoryIds: [],
    requiresShipping: true,
    isTaxable: true,
    minimumOrderQuantity: 1,
    maximumOrderQuantity: 100,
    isAgeRestricted: false,
    isPreOrder: false,
    isFeatured: false,
    isNewArrival: false,
    excludeFromDiscounts: false,
    noIndex: false,
    options: [],
    variants: [defaultVariant({})],
    media: [],
    metafields: [],
  };
}

function defaultVariant(optionValues: Record<string, string>): ProductVariantUpsertDto {
  return {
    price: 0,
    trackInventory: true,
    inventoryPolicy: 'Deny',
    lowStockThreshold: 5,
    requiresShipping: true,
    weightUnit: 'Kilogram',
    dimensionUnit: 'Centimeter',
    isActive: true,
    position: 0,
    optionValues,
    digitalDownloadLimit: 5,
    digitalDownloadExpiryDays: 30,
  };
}

function sigOf(values: Record<string, string>): string {
  return Object.entries(values || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k.trim()}:${v.trim()}`)
    .join('|');
}

function isPlaceholderVariant(variant?: ProductVariantUpsertDto | null): boolean {
  if (!variant) return false;
  return !variant.id && Object.keys(variant.optionValues || {}).length === 0;
}

function isVariantValidForOptions(
  variant: ProductVariantUpsertDto,
  options: ProductOptionUpsertDto[]
): boolean {
  if (options.length === 0) return true;
  if (isPlaceholderVariant(variant)) return false;
  const optValues = variant.optionValues || {};
  return options.every((opt) => {
    const optName = opt.name.trim().toLowerCase();
    if (!optName) return true;
    const matchingEntry = Object.entries(optValues).find(
      ([k]) => k.trim().toLowerCase() === optName
    );
    if (!matchingEntry) return false;
    const val = matchingEntry[1].trim().toLowerCase();
    return opt.values.some((ov) => ov.value.trim().toLowerCase() === val);
  });
}

export function ProductEditPage() {
  const { id } = useParams();
  const productId = id && id !== 'yeni' ? Number(id) : null;
  const isNew = productId === null;

  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<TabKey>('genel');
  const [form, setForm] = useState<ProductUpsertDto>(emptyProduct);
  const [isSlugCustomized, setIsSlugCustomized] = useState(!isNew);

  const existing = useQuery({
    queryKey: ['admin', 'product', productId],
    queryFn: () => adminProductsApi.getForEdit(productId!),
    enabled: productId !== null,
  });

  const categories = useQuery({
    queryKey: ['admin', 'categories'],
    queryFn: () => adminCategoriesApi.tree(),
    staleTime: 5 * 60_000,
  });

  const brands = useQuery({
    queryKey: ['admin', 'brands', 'all'],
    queryFn: () => adminBrandsApi.search({ pageSize: 100 }),
    staleTime: 5 * 60_000,
  });

  const taxClasses = useQuery({
    queryKey: ['admin', 'tax-classes'],
    queryFn: () => adminTaxApi.classes(),
    staleTime: 5 * 60_000,
  });

  // Uc, form sozlesmesinin kendisini donduyor; ek esleme gerekmez.
  useEffect(() => {
    if (existing.data) {
      setForm(existing.data);
      setIsSlugCustomized(true);
    }
  }, [existing.data]);

  const validateAndSanitizeForm = (): ProductUpsertDto | null => {
    if (!form.title.trim()) {
      toast.error('Lütfen ürün başlığını giriniz.');
      return null;
    }

    const payload: ProductUpsertDto = {
      ...form,
      title: form.title.trim(),
      categoryIds: form.primaryCategoryId ? [form.primaryCategoryId] : [],
    };

    // 1. Eksen kontrolü
    if (payload.options.length === 0) {
      // Tekil ürün: yalnızca 1 adet boş optionValues'a sahip varsayılan varyant olmalı
      const base = payload.variants[0] || defaultVariant({});
      payload.variants = [
        {
          ...base,
          title: null,
          position: 0,
          optionValues: {},
        },
      ];
    } else {
      // Çok varyantlı ürün:
      // Boş placeholder varyantları ve seçenek eksenleriyle uyuşmayan varyantları temizle
      let validVariants = payload.variants.filter((v) =>
        isVariantValidForOptions(v, payload.options)
      );

      // Eğer hiç varyant kalmadıysa kombinasyonlardan otomatik üret
      if (validVariants.length === 0) {
        if (combinations.length === 0) {
          toast.error('Tanımladığınız seçenek eksenleri için en az bir değer giriniz.');
          return null;
        }
        const template = payload.variants[0] || defaultVariant({});
        validVariants = combinations.map((comb, idx) => ({
          ...defaultVariant(comb),
          price: template.price || 0,
          compareAtPrice: template.compareAtPrice || null,
          costPrice: template.costPrice || null,
          initialStock: template.initialStock ?? null,
          weight: template.weight ?? null,
          position: idx,
        }));
      }

      // Her varyantın tanımlı tüm eksenleri içerdiğini kontrol et
      for (const opt of payload.options) {
        const optName = opt.name.trim();
        if (!optName) {
          toast.error('Lütfen tüm seçenek eksenlerinin adını giriniz.');
          return null;
        }
      }

      payload.variants = validVariants;
    }

    // 2. Fiyat kontrolleri
    for (const v of payload.variants) {
      if (v.price < 0) {
        toast.error('Varyant fiyatı 0 veya daha büyük olmalıdır.');
        return null;
      }
      if (v.compareAtPrice !== null && v.compareAtPrice !== undefined && v.compareAtPrice < v.price) {
        toast.error('İndirimli fiyat normal satış fiyatından düşük olamaz.');
        return null;
      }
    }

    return payload;
  };

  const save = useMutation({
    mutationFn: () => {
      const sanitized = validateAndSanitizeForm();
      if (!sanitized) {
        return Promise.reject(new Error('Form doğrulanamadı.'));
      }
      return isNew ? adminProductsApi.create(sanitized) : adminProductsApi.update(productId!, sanitized);
    },
    onSuccess: (product) => {
      toast.success(isNew ? 'Ürün oluşturuldu.' : 'Ürün güncellendi.');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'product', productId] });
      if (isNew) navigate(`/urunler/${product.id}`, { replace: true });
    },
  });

  const set = <K extends keyof ProductUpsertDto>(key: K, value: ProductUpsertDto[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const updateVariant = (index: number, patch: Partial<ProductVariantUpsertDto>) =>
    setForm((current) => {
      const baseVariants = current.variants.length > 0 ? current.variants : [defaultVariant({})];
      return {
        ...current,
        variants: baseVariants.map((variant, i) => (i === index ? { ...variant, ...patch } : variant)),
      };
    });

  /** Seceneklerin kartezyen carpimini uretir. */
  const combinations = useMemo(() => {
    const active = form.options.filter(
      (option) => option.name.trim() !== '' && option.values.length > 0
    );
    if (active.length === 0) return [] as Record<string, string>[];

    return active.reduce<Record<string, string>[]>(
      (accumulator, option) =>
        accumulator.flatMap((partial) =>
          option.values
            .map((v) => v.value.trim())
            .filter(Boolean)
            .map((val) => ({ ...partial, [option.name.trim()]: val }))
        ),
      [{}]
    );
  }, [form.options]);

  /** Kombinasyonlari varyant listesine yansit; mevcut fiyat ve verileri koru. */
  const regenerateVariants = (mode: 'all' | 'missingOnly' = 'missingOnly') => {
    if (combinations.length === 0) {
      setForm((current) => ({
        ...current,
        variants: current.variants.length > 0 ? current.variants : [defaultVariant({})],
      }));
      return;
    }

    setForm((current) => {
      const template = current.variants[0] || defaultVariant({});
      const basePrice = template.price || 0;
      const baseCompareAtPrice = template.compareAtPrice || null;
      const baseCostPrice = template.costPrice || null;
      const baseStock = template.initialStock ?? null;
      const baseWeight = template.weight ?? null;
      const baseWeightUnit = template.weightUnit || 'Kilogram';

      const existingMap = new Map<string, ProductVariantUpsertDto>();
      for (const v of current.variants) {
        if (!isPlaceholderVariant(v)) {
          existingMap.set(sigOf(v.optionValues), v);
        }
      }

      if (mode === 'missingOnly') {
        const kept = current.variants.filter((v) => !isPlaceholderVariant(v));
        const newVariants = [...kept];

        combinations.forEach((comb) => {
          const sig = sigOf(comb);
          if (!existingMap.has(sig)) {
            newVariants.push({
              ...defaultVariant(comb),
              price: basePrice,
              compareAtPrice: baseCompareAtPrice,
              costPrice: baseCostPrice,
              initialStock: baseStock,
              weight: baseWeight,
              weightUnit: baseWeightUnit,
              position: newVariants.length,
            });
          }
        });

        const result = newVariants.length > 0
          ? newVariants
          : combinations.map((comb, idx) => ({
              ...defaultVariant(comb),
              price: basePrice,
              compareAtPrice: baseCompareAtPrice,
              costPrice: baseCostPrice,
              initialStock: baseStock,
              weight: baseWeight,
              weightUnit: baseWeightUnit,
              position: idx,
            }));

        return { ...current, variants: result };
      } else {
        const next = combinations.map((values, index) => {
          const sig = sigOf(values);
          const match = existingMap.get(sig);
          return match
            ? { ...match, position: index, optionValues: values }
            : {
                ...defaultVariant(values),
                price: basePrice,
                compareAtPrice: baseCompareAtPrice,
                costPrice: baseCostPrice,
                initialStock: baseStock,
                weight: baseWeight,
                weightUnit: baseWeightUnit,
                position: index,
              };
        });

        return { ...current, variants: next };
      }
    });

    toast.success('Varyantlar güncellendi.');
  };

  if (productId !== null && existing.isLoading) return <PageLoader />;

  return (
    <div>
      <PageHeader
        title={isNew ? 'Yeni ürün' : form.title || 'Ürünü düzenle'}
        description={isNew ? 'Ürün bilgilerini girin' : `Ürün #${productId}`}
        actions={
          <>
            <Link to="/urunler" className="btn-secondary">
              Listeye dön
            </Link>
            <button
              type="button"
              className="btn-primary"
              onClick={() => save.mutate()}
              disabled={save.isPending || !form.title}
            >
              {save.isPending ? <Spinner className="h-4 w-4" /> : null}
              Kaydet
            </button>
          </>
        }
      />

      <div className="mb-5">
        <FormErrors error={save.error} />
      </div>

      {/* Sekmeler */}
      <div className="mb-5 flex gap-1 overflow-x-auto border-b border-ink-200">
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setTab(item.key)}
            className={`whitespace-nowrap border-b-2 px-4 py-2.5 text-sm transition ${
              tab === item.key
                ? 'border-ink-900 font-medium text-ink-900'
                : 'border-transparent text-ink-500 hover:text-ink-800'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'genel' ? (
        <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
          <div className="space-y-5">
            <section className="card p-5">
              <h2 className="mb-4 text-sm font-semibold text-ink-900">Temel bilgiler</h2>

              <div className="space-y-4">
                <Field label="Başlık" htmlFor="p-title" required>
                  <input
                    id="p-title"
                    className="field"
                    value={form.title}
                    onChange={(event) => {
                      const newTitle = event.target.value;
                      setForm((current) => ({
                        ...current,
                        title: newTitle,
                        slug: !isSlugCustomized ? slugify(newTitle) : current.slug,
                      }));
                    }}
                    placeholder="Örn: Medine Mebrum Hurması 1 Kg"
                  />
                </Field>

                <Field
                  label="URL adresi (slug)"
                  htmlFor="p-slug"
                  hint={
                    <span className="flex items-center justify-between text-xs">
                      <span>
                        {!isSlugCustomized
                          ? 'Başlıktan otomatik üretiliyor.'
                          : 'Özel olarak düzenlendi.'}
                      </span>
                      <button
                        type="button"
                        className="font-medium text-brand-600 hover:text-brand-700 hover:underline"
                        onClick={() => {
                          setIsSlugCustomized(false);
                          set('slug', slugify(form.title));
                        }}
                      >
                        Başlıktan yeniden üret
                      </button>
                    </span>
                  }
                >
                  <input
                    id="p-slug"
                    className="field font-mono text-sm"
                    value={form.slug ?? ''}
                    onChange={(event) => {
                      setIsSlugCustomized(true);
                      set('slug', event.target.value.toLowerCase().replace(/\s+/g, '-'));
                    }}
                    onBlur={(event) => {
                      if (event.target.value) {
                        set('slug', slugify(event.target.value));
                      }
                    }}
                    placeholder="medine-mebrum-hurmasi-1-kg"
                  />
                </Field>

                <Field label="Kısa açıklama" htmlFor="p-short">
                  <textarea
                    id="p-short"
                    className="field min-h-20"
                    maxLength={500}
                    value={form.shortDescription ?? ''}
                    onChange={(event) => set('shortDescription', event.target.value)}
                  />
                </Field>

                <Field
                  label="Açıklama"
                  htmlFor="p-desc"
                  hint="Zengin metin editörü ile görsel biçimlendirme yapabilir veya istediğinizde HTML kaynak koduna geçebilirsiniz."
                >
                  <RichTextEditor
                    id="p-desc"
                    value={form.description ?? ''}
                    onChange={(html) => set('description', html)}
                    placeholder="Ürün detaylı açıklamasını girin..."
                    minHeight="min-h-[260px]"
                  />
                </Field>
              </div>
            </section>

            {form.options.length === 0 ? (
              <>
                {/* Tekil Ürün Fiyat & Stok Bilgileri */}
                <section className="card p-5">
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <h2 className="text-sm font-semibold text-ink-900">
                        Fiyat, Stok ve Kod Bilgileri
                      </h2>
                      <p className="mt-0.5 text-xs text-ink-500">
                        Bu ürün tek varyantlıdır. Ürünün fiyat, stok, SKU ve barkodunu buradan belirleyebilirsiniz.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setTab('varyantlar')}
                      className="text-xs font-medium text-blue-600 hover:underline"
                    >
                      Varyant ekle (Renk/Beden) &rarr;
                    </button>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="SKU (Stok Kodu)" htmlFor="p-sku" hint="Örn: URUN-001">
                      <input
                        id="p-sku"
                        className="field font-mono text-sm"
                        placeholder="Otomatik üretilir veya girin"
                        value={form.variants[0]?.sku ?? ''}
                        onChange={(event) => updateVariant(0, { sku: event.target.value })}
                      />
                    </Field>

                    <Field label="Barkod (EAN / GTIN)" htmlFor="p-barcode" hint="Örn: 868000000001">
                      <input
                        id="p-barcode"
                        className="field font-mono text-sm"
                        placeholder="Barkod numarası"
                        value={form.variants[0]?.barcode ?? ''}
                        onChange={(event) => updateVariant(0, { barcode: event.target.value })}
                      />
                    </Field>

                    <Field label="Satış Fiyatı (TRY)" htmlFor="p-price" required>
                      <input
                        id="p-price"
                        type="number"
                        step="0.01"
                        min={0}
                        className="field"
                        value={form.variants[0]?.price ?? 0}
                        onChange={(event) =>
                          updateVariant(0, { price: Number(event.target.value) })
                        }
                      />
                    </Field>

                    <Field
                      label="İndirimli Fiyat (Üstü Çizili)"
                      htmlFor="p-compare"
                      hint="Fiyattan yüksekse indirim rozeti gösterilir"
                    >
                      <input
                        id="p-compare"
                        type="number"
                        step="0.01"
                        min={0}
                        className="field"
                        placeholder="Örn: 1499.90"
                        value={form.variants[0]?.compareAtPrice ?? ''}
                        onChange={(event) =>
                          updateVariant(0, {
                            compareAtPrice: event.target.value ? Number(event.target.value) : null,
                          })
                        }
                      />
                    </Field>

                    <Field
                      label="Alış Fiyatı (Maliyet)"
                      htmlFor="p-cost"
                      hint="Yalnızca raporlarda kâr hesabı için"
                    >
                      <input
                        id="p-cost"
                        type="number"
                        step="0.01"
                        min={0}
                        className="field"
                        placeholder="Maliyet"
                        value={form.variants[0]?.costPrice ?? ''}
                        onChange={(event) =>
                          updateVariant(0, {
                            costPrice: event.target.value ? Number(event.target.value) : null,
                          })
                        }
                      />
                    </Field>

                    <Field
                      label="Başlangıç Stoğu"
                      htmlFor="p-stock"
                      hint={
                        form.variants[0]?.id
                          ? 'Mevcut stoğu Stok menüsünden güncelleyebilirsiniz'
                          : 'İlk stok miktarı'
                      }
                    >
                      <input
                        id="p-stock"
                        type="number"
                        min={0}
                        disabled={Boolean(form.variants[0]?.id)}
                        className="field"
                        value={form.variants[0]?.initialStock ?? ''}
                        onChange={(event) =>
                          updateVariant(0, {
                            initialStock: event.target.value ? Number(event.target.value) : null,
                          })
                        }
                      />
                    </Field>
                  </div>
                </section>

                {/* Kargo & Boyut Bilgileri */}
                <section className="card p-5">
                  <h2 className="mb-1 text-sm font-semibold text-ink-900">
                    Kargo, Ağırlık ve Boyutlar
                  </h2>
                  <p className="mb-4 text-xs text-ink-500">
                    Desi ve kargo ücreti hesaplamalarında kullanılır.
                  </p>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Ürün Ağırlığı" htmlFor="p-weight">
                      <div className="flex gap-2">
                        <input
                          id="p-weight"
                          type="number"
                          step="0.01"
                          min={0}
                          className="field flex-1"
                          placeholder="0.00"
                          value={form.variants[0]?.weight ?? ''}
                          onChange={(event) =>
                            updateVariant(0, {
                              weight: event.target.value ? Number(event.target.value) : null,
                            })
                          }
                        />
                        <select
                          className="field w-28"
                          value={form.variants[0]?.weightUnit ?? 'Gram'}
                          onChange={(event) =>
                            updateVariant(0, { weightUnit: event.target.value as any })
                          }
                        >
                          <option value="Gram">Gram (g)</option>
                          <option value="Kilogram">Kilogram (kg)</option>
                        </select>
                      </div>
                    </Field>

                    <Field label="Boyut Birimi" htmlFor="p-dim-unit">
                      <select
                        id="p-dim-unit"
                        className="field"
                        value={form.variants[0]?.dimensionUnit ?? 'Centimeter'}
                        onChange={(event) =>
                          updateVariant(0, { dimensionUnit: event.target.value as any })
                        }
                      >
                        <option value="Centimeter">Santimetre (cm)</option>
                        <option value="Meter">Metre (m)</option>
                        <option value="Inch">İnç (in)</option>
                      </select>
                    </Field>
                  </div>

                  <div className="mt-4 grid grid-cols-3 gap-3">
                    <Field label="En (Genişlik)" htmlFor="p-width">
                      <input
                        id="p-width"
                        type="number"
                        step="0.1"
                        min={0}
                        className="field"
                        placeholder="En"
                        value={form.variants[0]?.width ?? ''}
                        onChange={(event) =>
                          updateVariant(0, {
                            width: event.target.value ? Number(event.target.value) : null,
                          })
                        }
                      />
                    </Field>
                    <Field label="Boy (Uzunluk)" htmlFor="p-length">
                      <input
                        id="p-length"
                        type="number"
                        step="0.1"
                        min={0}
                        className="field"
                        placeholder="Boy"
                        value={form.variants[0]?.length ?? ''}
                        onChange={(event) =>
                          updateVariant(0, {
                            length: event.target.value ? Number(event.target.value) : null,
                          })
                        }
                      />
                    </Field>
                    <Field label="Yükseklik" htmlFor="p-height">
                      <input
                        id="p-height"
                        type="number"
                        step="0.1"
                        min={0}
                        className="field"
                        placeholder="Yükseklik"
                        value={form.variants[0]?.height ?? ''}
                        onChange={(event) =>
                          updateVariant(0, {
                            height: event.target.value ? Number(event.target.value) : null,
                          })
                        }
                      />
                    </Field>
                  </div>
                </section>
              </>
            ) : (
              <section className="card border-blue-200 bg-blue-50/50 p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-blue-900">
                      Çoklu Varyantlı Ürün ({form.variants.length} Varyant)
                    </h3>
                    <p className="mt-0.5 text-xs text-blue-700">
                      Bu üründe varyant eksenleri tanımlıdır. Fiyat, barkod, SKU, stok, ağırlık ve boyut bilgileri her varyant için ayrı ayrı yönetilmektedir.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setTab('varyantlar')}
                    className="btn-primary btn-sm"
                  >
                    Varyantları Yönet &rarr;
                  </button>
                </div>
              </section>
            )}

            <section className="card p-5">
              <h2 className="mb-4 text-sm font-semibold text-ink-900">Satış kuralları</h2>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="En az sipariş adedi" htmlFor="p-min">
                  <input
                    id="p-min"
                    type="number"
                    min={1}
                    className="field"
                    value={form.minimumOrderQuantity}
                    onChange={(event) => set('minimumOrderQuantity', Number(event.target.value))}
                  />
                </Field>
                <Field label="En çok sipariş adedi" htmlFor="p-max">
                  <input
                    id="p-max"
                    type="number"
                    min={1}
                    className="field"
                    value={form.maximumOrderQuantity}
                    onChange={(event) => set('maximumOrderQuantity', Number(event.target.value))}
                  />
                </Field>
              </div>

              <div className="mt-4 space-y-2.5">
                <Toggle
                  label="Kargo gerektirir"
                  checked={form.requiresShipping}
                  onChange={(value) => set('requiresShipping', value)}
                />
                <Toggle
                  label="Vergiye tabi"
                  checked={form.isTaxable}
                  onChange={(value) => set('isTaxable', value)}
                />
                <Toggle
                  label="Öne çıkar"
                  checked={form.isFeatured}
                  onChange={(value) => set('isFeatured', value)}
                />
                <Toggle
                  label="Yeni ürün rozeti"
                  checked={form.isNewArrival}
                  onChange={(value) => set('isNewArrival', value)}
                />
                <Toggle
                  label="İndirimlerden hariç tut"
                  checked={form.excludeFromDiscounts}
                  onChange={(value) => set('excludeFromDiscounts', value)}
                />
              </div>

              {form.isPreOrder ? (
                <Field label="Tahmini gönderim tarihi" htmlFor="p-preorder" className="mt-4 max-w-xs">
                  <input
                    id="p-preorder"
                    type="date"
                    className="field"
                    value={form.preOrderReleaseDate?.slice(0, 10) ?? ''}
                    onChange={(event) => set('preOrderReleaseDate', event.target.value || null)}
                  />
                </Field>
              ) : null}

              {form.isAgeRestricted ? (
                <Field label="Alt yaş sınırı" htmlFor="p-age" className="mt-4 max-w-xs">
                  <input
                    id="p-age"
                    type="number"
                    min={0}
                    className="field"
                    value={form.minimumAge ?? 18}
                    onChange={(event) => set('minimumAge', Number(event.target.value))}
                  />
                </Field>
              ) : null}
            </section>
          </div>

          <div className="space-y-5">
            <section className="card p-5">
              <h2 className="mb-4 text-sm font-semibold text-ink-900">Yayın</h2>

              <Field label="Durum" htmlFor="p-status">
                <select
                  id="p-status"
                  className="field"
                  value={form.status}
                  onChange={(event) => set('status', event.target.value as ProductStatus)}
                >
                  {productStatusValues.map((value) => (
                    <option key={value} value={value}>
                      {productStatusLabels[value].text}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Yayın tarihi" htmlFor="p-published" className="mt-4">
                <input
                  id="p-published"
                  type="date"
                  className="field"
                  value={form.publishedAt?.slice(0, 10) ?? ''}
                  onChange={(event) => set('publishedAt', event.target.value || null)}
                />
              </Field>
            </section>

            <section className="card p-5">
              <h2 className="mb-4 text-sm font-semibold text-ink-900">Sınıflandırma</h2>

              <div className="space-y-4">
                <Field label="Ürün tipi" htmlFor="p-kind">
                  <select
                    id="p-kind"
                    className="field"
                    value={form.kind}
                    onChange={(event) =>
                      set('kind', event.target.value as ProductUpsertDto['kind'])
                    }
                  >
                    {productKindValues.map((value) => (
                      <option key={value} value={value}>
                        {productKindLabels[value]}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Marka" htmlFor="p-brand">
                  <select
                    id="p-brand"
                    className="field"
                    value={form.brandId ?? ''}
                    onChange={(event) =>
                      set('brandId', event.target.value ? Number(event.target.value) : null)
                    }
                  >
                    <option value="">Marka yok</option>
                    {(brands.data?.items ?? []).map((brand) => (
                      <option key={brand.id} value={brand.id}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field
                  label="Kategori / Alt Kategori"
                  htmlFor="p-category"
                  hint="Ana kategori veya alt kategoriyi ağaçtan seçin (örn: Moda > Ayakkabı)"
                >
                  <select
                    id="p-category"
                    className="field"
                    value={form.primaryCategoryId ?? ''}
                    onChange={(event) => {
                      const value = event.target.value ? Number(event.target.value) : null;
                      setForm((current) => ({
                        ...current,
                        primaryCategoryId: value,
                        categoryIds: value ? [value] : [],
                      }));
                    }}
                  >
                    <option value="">Kategori seçiniz</option>
                    {flattenCategories(categories.data ?? []).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="KDV / Vergi Sınıfı" htmlFor="p-tax" hint="Uygulanacak KDV oranını belirler">
                  <select
                    id="p-tax"
                    className="field"
                    value={form.taxClassId ?? ''}
                    onChange={(event) =>
                      set('taxClassId', event.target.value ? Number(event.target.value) : null)
                    }
                  >
                    <option value="">Varsayılan</option>
                    {(taxClasses.data ?? []).map((taxClass) => (
                      <option key={taxClass.id} value={taxClass.id}>
                        {taxClass.name}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Ürün türü" htmlFor="p-type" hint="Örn. Tişört, Ayakkabı">
                  <input
                    id="p-type"
                    className="field"
                    value={form.productType ?? ''}
                    onChange={(event) => set('productType', event.target.value)}
                  />
                </Field>

                <Field label="Etiketler" htmlFor="p-tags" hint="Virgülle ayırın.">
                  <input
                    id="p-tags"
                    className="field"
                    value={form.tags ?? ''}
                    onChange={(event) => set('tags', event.target.value)}
                  />
                </Field>
              </div>
            </section>
          </div>
        </div>
      ) : null}

      {tab === 'varyantlar' ? (
        <VariantsTab
          form={form}
          setForm={setForm}
          combinations={combinations}
          onRegenerate={regenerateVariants}
        />
      ) : null}

      {tab === 'gorseller' ? <MediaTab form={form} setForm={setForm} /> : null}

      {tab === 'seo' ? (
        <section className="card max-w-2xl p-5 space-y-6">
          <div>
            <h2 className="text-sm font-semibold text-ink-900">Arama motoru görünümü (SEO)</h2>
            <p className="mt-1 text-xs text-ink-500">
              Ürününüzün Google arama sonuçlarında nasıl görüneceğini canlı olarak inceleyin ve özelleştirin.
            </p>
          </div>

          <GoogleSearchPreview
            title={form.seoTitle}
            fallbackTitle={form.title}
            description={form.seoDescription}
            fallbackDescription={form.shortDescription || form.description?.replace(/<[^>]*>/g, '')}
            urlPath={`urun/${form.slug || 'urun-adi'}`}
            noIndex={form.noIndex}
          />

          <div className="space-y-4 pt-2 border-t border-ink-100">
            <Field label="SEO başlığı" htmlFor="p-seo-title" hint="Boşsa ürün başlığı kullanılır.">
              <input
                id="p-seo-title"
                className="field"
                maxLength={200}
                value={form.seoTitle ?? ''}
                onChange={(event) => set('seoTitle', event.target.value)}
              />
            </Field>

            <Field label="SEO açıklaması" htmlFor="p-seo-desc">
              <textarea
                id="p-seo-desc"
                className="field min-h-24"
                maxLength={320}
                value={form.seoDescription ?? ''}
                onChange={(event) => set('seoDescription', event.target.value)}
              />
            </Field>

            <Field label="Anahtar kelimeler" htmlFor="p-seo-keys">
              <input
                id="p-seo-keys"
                className="field"
                value={form.seoKeywords ?? ''}
                onChange={(event) => set('seoKeywords', event.target.value)}
              />
            </Field>

            <Toggle
              label="Arama motorlarında indekslenmesin (noindex)"
              checked={form.noIndex}
              onChange={(value) => set('noIndex', value)}
            />
          </div>
        </section>
      ) : null}
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-ink-700">
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-ink-300 text-brand-600"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      {label}
    </label>
  );
}

/* --------------------------- Varyant sekmesi --------------------------- */

const COLOR_SUGGESTIONS: Record<string, string> = {
  siyah: '#18181b',
  black: '#18181b',
  'puma siyahı': '#1f2421',
  'siyah titanyum': '#2e2e2e',
  'doğal titanyum': '#9a9590',
  'mavi titanyum': '#3b4450',
  'beyaz titanyum': '#e5e5e5',
  beyaz: '#ffffff',
  white: '#ffffff',
  kırmızı: '#dc2626',
  red: '#dc2626',
  'ateş kırmızısı': '#dc2626',
  mavi: '#2563eb',
  blue: '#2563eb',
  lacivert: '#1e3a8a',
  navy: '#1e3a8a',
  yeşil: '#16a34a',
  green: '#16a34a',
  haki: '#556b2f',
  sarı: '#eab308',
  yellow: '#eab308',
  turuncu: '#ea580c',
  orange: '#ea580c',
  mor: '#9333ea',
  purple: '#9333ea',
  pembe: '#ec4899',
  pink: '#ec4899',
  gri: '#6b7280',
  gray: '#6b7280',
  grey: '#6b7280',
  gümüş: '#c0c0c0',
  silver: '#c0c0c0',
  altın: '#d4af37',
  gold: '#d4af37',
  kahverengi: '#78350f',
  brown: '#78350f',
  bej: '#f5f5dc',
  beige: '#f5f5dc',
  antrasit: '#334155',
  bordo: '#800020',
};

function suggestColor(value: string): string {
  const normalized = value.trim().toLowerCase();
  if (COLOR_SUGGESTIONS[normalized]) return COLOR_SUGGESTIONS[normalized];
  for (const [key, hex] of Object.entries(COLOR_SUGGESTIONS)) {
    if (normalized.includes(key)) return hex;
  }
  return '#94a3b8';
}

function VariantsTab({
  form,
  setForm,
  combinations,
  onRegenerate,
}: {
  form: ProductUpsertDto;
  setForm: React.Dispatch<React.SetStateAction<ProductUpsertDto>>;
  combinations: Record<string, string>[];
  onRegenerate: (mode?: 'all' | 'missingOnly') => void;
}) {
  const toast = useToast();
  const [pickerVariantIndex, setPickerVariantIndex] = useState<number | null>(null);
  const [uploadingVariantImage, setUploadingVariantImage] = useState(false);
  const variantFileInputRef = useRef<HTMLInputElement | null>(null);

  const [attributePickerOpen, setAttributePickerOpen] = useState(false);
  const [showBulkBar, setShowBulkBar] = useState(false);
  const [bulkPrice, setBulkPrice] = useState<string>('');
  const [bulkCompareAtPrice, setBulkCompareAtPrice] = useState<string>('');
  const [bulkStock, setBulkStock] = useState<string>('');

  const attributesQuery = useQuery({
    queryKey: ['admin-attributes'],
    queryFn: () => adminAttributesApi.getAll(true),
  });

  const addFromAttribute = (attr: ProductAttributeDto) => {
    if (form.options.length >= 3) {
      toast.error('En fazla 3 eksen ekleyebilirsiniz.');
      return;
    }
    const alreadyExists = form.options.some((o) => o.name.toLowerCase() === attr.name.toLowerCase());
    if (alreadyExists) {
      toast.error(`"${attr.name}" ekseni zaten eklenmiş.`);
      return;
    }

    const mappedDisplayType = attr.displayType === 'color' ? 'swatch' : (attr.displayType === 'button' ? 'button' : 'dropdown');

    const newOption: ProductOptionUpsertDto = {
      name: attr.name,
      displayType: mappedDisplayType,
      position: form.options.length,
      values: attr.values.map((v, i) => ({
        value: v.value,
        colorCode: v.colorCode ?? null,
        position: v.position || i + 1,
      })),
    };

    setForm((current) => {
      const nextOptions = [...current.options, newOption];
      let nextVariants = current.variants;

      if (current.variants.length === 1 && isPlaceholderVariant(current.variants[0])) {
        const template = current.variants[0];
        const activeOpts = nextOptions.filter((o) => o.name.trim() !== '' && o.values.length > 0);
        if (activeOpts.length > 0) {
          const autoCombs = activeOpts.reduce<Record<string, string>[]>(
            (acc, opt) =>
              acc.flatMap((part) =>
                opt.values
                  .map((v) => v.value.trim())
                  .filter(Boolean)
                  .map((val) => ({ ...part, [opt.name.trim()]: val }))
              ),
            [{}]
          );
          if (autoCombs.length > 0) {
            nextVariants = autoCombs.map((comb, i) => ({
              ...defaultVariant(comb),
              price: template.price,
              compareAtPrice: template.compareAtPrice,
              costPrice: template.costPrice,
              initialStock: template.initialStock,
              weight: template.weight,
              position: i,
            }));
          }
        }
      }

      return {
        ...current,
        options: nextOptions,
        variants: nextVariants,
      };
    });

    toast.success(`"${attr.name}" ekseni ${attr.values.length} değerle eklendi.`);
    setAttributePickerOpen(false);
  };

  const addOption = () => {
    if (form.options.length >= 3) {
      toast.error('En fazla 3 eksen ekleyebilirsiniz.');
      return;
    }
    setForm((current) => ({
      ...current,
      options: [
        ...current.options,
        {
          name: '',
          displayType: 'dropdown',
          position: current.options.length,
          values: [],
        } satisfies ProductOptionUpsertDto,
      ],
    }));
  };

  const updateOption = (index: number, patch: Partial<ProductOptionUpsertDto>) => {
    setForm((current) => {
      const oldOption = current.options[index];
      const newOptions = current.options.map((opt, i) => (i === index ? { ...opt, ...patch } : opt));

      let newVariants = current.variants;

      // 1. Eksen adı değiştirildiyse (Rename), varyantların optionValues anahtarlarını senkronize et
      if (patch.name !== undefined && oldOption && oldOption.name && patch.name !== oldOption.name) {
        const oldName = oldOption.name;
        const newName = patch.name;
        newVariants = current.variants.map((v) => {
          if (!v.optionValues || !(oldName in v.optionValues)) return v;
          const updatedValues = { ...v.optionValues };
          const val = updatedValues[oldName];
          delete updatedValues[oldName];
          if (newName.trim() !== '') {
            updatedValues[newName] = val;
          }
          return {
            ...v,
            optionValues: updatedValues,
          };
        });
      }

      // 2. Eğer değerler güncellendiyse (values):
      if (patch.values !== undefined) {
        const activeOpts = newOptions.filter((o) => o.name.trim() !== '' && o.values.length > 0);
        if (activeOpts.length > 0) {
          if (newVariants.length === 1 && isPlaceholderVariant(newVariants[0])) {
            const template = newVariants[0];
            const autoCombs = activeOpts.reduce<Record<string, string>[]>(
              (acc, opt) =>
                acc.flatMap((part) =>
                  opt.values
                    .map((v) => v.value.trim())
                    .filter(Boolean)
                    .map((val) => ({ ...part, [opt.name.trim()]: val }))
                ),
              [{}]
            );
            if (autoCombs.length > 0) {
              newVariants = autoCombs.map((comb, i) => ({
                ...defaultVariant(comb),
                price: template.price,
                compareAtPrice: template.compareAtPrice,
                costPrice: template.costPrice,
                initialStock: template.initialStock,
                weight: template.weight,
                position: i,
              }));
            }
          } else {
            // Silinen değerleri kullanan veya eksenle uyuşmayan varyantları otomatik filtrele
            const pruned = newVariants.filter((v) => isVariantValidForOptions(v, newOptions));
            if (pruned.length > 0) {
              newVariants = pruned;
            }
          }
        }
      }

      return {
        ...current,
        options: newOptions,
        variants: newVariants,
      };
    });
  };

  const removeOption = (index: number) => {
    setForm((current) => {
      const targetOption = current.options[index];
      const newOptions = current.options.filter((_, i) => i !== index);

      // Tüm eksenler silindiyse: ürünü temiz tekil ürüne dönüştür
      if (newOptions.length === 0) {
        const template = current.variants[0] || defaultVariant({});
        return {
          ...current,
          options: [],
          variants: [
            {
              ...template,
              title: null,
              position: 0,
              optionValues: {},
            },
          ],
        };
      }

      // Diğer eksenler duruyorsa, silinen eksenin anahtarını varyantlardan çıkar
      const optionName = targetOption?.name;
      const strippedVariants = current.variants.map((v) => {
        if (!optionName || !(optionName in v.optionValues)) return v;
        const updated = { ...v.optionValues };
        delete updated[optionName];
        return {
          ...v,
          optionValues: updated,
        };
      });

      // Tekilleştir
      const seen = new Set<string>();
      const deduplicated: ProductVariantUpsertDto[] = [];
      for (const v of strippedVariants) {
        const sig = sigOf(v.optionValues);
        if (!seen.has(sig)) {
          seen.add(sig);
          deduplicated.push(v);
        }
      }

      return {
        ...current,
        options: newOptions,
        variants: deduplicated.length > 0 ? deduplicated : [defaultVariant({})],
      };
    });

    toast.info('Seçenek ekseni kaldırıldı ve varyantlar güncellendi.');
  };

  const updateVariant = (index: number, patch: Partial<ProductVariantUpsertDto>) =>
    setForm((current) => ({
      ...current,
      variants: current.variants.map((variant, i) =>
        i === index ? { ...variant, ...patch } : variant,
      ),
    }));

  const removeVariant = (index: number) => {
    const variantToRemove = form.variants[index];
    if (!variantToRemove) return;

    if (form.options.length === 0) {
      toast.error('Tekil ürünün ana varyantı silinemez. Fiyat ve stok bu varyant üzerinden yönetilir.');
      return;
    }

    if (form.variants.length <= 1) {
      toast.error('En az bir varyant kalmalıdır. Varyantları tamamen kaldırmak için seçenek eksenlerini silebilirsiniz.');
      return;
    }

    const label = Object.values(variantToRemove.optionValues).join(' / ') || (variantToRemove.title ?? 'Varyant');

    setForm((current) => ({
      ...current,
      variants: current.variants.filter((_, i) => i !== index),
    }));

    toast.success(`"${label}" varyantı kaldırıldı.`);
  };

  const addSingleCombination = (comb: Record<string, string>) => {
    setForm((current) => {
      const template = current.variants[0] || defaultVariant({});
      const newVariant: ProductVariantUpsertDto = {
        ...defaultVariant(comb),
        price: template.price || 0,
        compareAtPrice: template.compareAtPrice || null,
        costPrice: template.costPrice || null,
        initialStock: template.initialStock ?? null,
        weight: template.weight ?? null,
        weightUnit: template.weightUnit || 'Kilogram',
        position: current.variants.length,
      };

      if (current.variants.length === 1 && isPlaceholderVariant(current.variants[0])) {
        return {
          ...current,
          variants: [newVariant],
        };
      }

      return {
        ...current,
        variants: [...current.variants, newVariant],
      };
    });

    toast.success(`"${Object.values(comb).join(' / ')}" varyantı eklendi.`);
  };

  const cleanInvalidVariants = () => {
    setForm((current) => {
      const validVariants = current.variants.filter((v) =>
        isVariantValidForOptions(v, current.options)
      );

      return {
        ...current,
        variants: validVariants.length > 0 ? validVariants : [defaultVariant({})],
      };
    });

    toast.success('Geçersiz varyantlar temizlendi.');
  };

  const applyBulkUpdates = () => {
    if (bulkPrice === '' && bulkCompareAtPrice === '' && bulkStock === '') {
      toast.error('Lütfen uygulanacak en az bir alan giriniz (Fiyat veya Stok).');
      return;
    }

    setForm((current) => ({
      ...current,
      variants: current.variants.map((v) => {
        const patch: Partial<ProductVariantUpsertDto> = {};
        if (bulkPrice !== '') patch.price = Number(bulkPrice);
        if (bulkCompareAtPrice !== '') patch.compareAtPrice = bulkCompareAtPrice ? Number(bulkCompareAtPrice) : null;
        if (bulkStock !== '') patch.initialStock = bulkStock !== '' ? Number(bulkStock) : null;
        return { ...v, ...patch };
      }),
    }));

    toast.success('Tüm varyantlara değerler uygulandı.');
  };

  const handleVariantFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0 || pickerVariantIndex === null) return;
    const file = files[0];
    setUploadingVariantImage(true);
    try {
      const result = await adminFilesApi.uploadImage(file, 'products');
      if (!result.success || !result.url) {
        toast.error(result.errorMessage ?? 'Görsel yüklenemedi.');
        return;
      }

      const newMediaItem = {
        kind: 'Image' as const,
        url: result.url,
        thumbnailUrl: result.thumbnailUrl ?? null,
        altText: result.fileName ?? null,
        position: form.media.length,
        isPrimary: form.media.length === 0,
      };

      setForm((current) => ({
        ...current,
        media: [...current.media, newMediaItem],
        variants: current.variants.map((v, i) =>
          i === pickerVariantIndex ? { ...v, imageUrl: result.url } : v,
        ),
      }));

      toast.success('Görsel yüklendi ve varyanta atandı.');
      setPickerVariantIndex(null);
    } catch (error) {
      toast.fromError(error, 'Görsel yüklenemedi.');
    } finally {
      setUploadingVariantImage(false);
      if (variantFileInputRef.current) {
        variantFileInputRef.current.value = '';
      }
    }
  };

  const handleSelectMediaForVariant = (imageUrl: string) => {
    if (pickerVariantIndex === null) return;
    updateVariant(pickerVariantIndex, { imageUrl });
    setPickerVariantIndex(null);
    toast.success('Varyant görseli atandı.');
  };

  const handleRemoveVariantImage = () => {
    if (pickerVariantIndex === null) return;
    updateVariant(pickerVariantIndex, { imageUrl: null });
    setPickerVariantIndex(null);
    toast.info('Varyant görseli kaldırıldı.');
  };

  // Metrikler ve Durumlar
  const currentSignatures = new Set(
    form.variants
      .filter((v) => !isPlaceholderVariant(v))
      .map((v) => sigOf(v.optionValues))
  );

  const missingCombinations = combinations.filter((comb) => !currentSignatures.has(sigOf(comb)));
  const hasPlaceholder = form.variants.some((v) => isPlaceholderVariant(v));

  const invalidVariantsCount = form.variants.filter((v) => {
    if (form.options.length === 0) return false;
    return !isPlaceholderVariant(v) && !isVariantValidForOptions(v, form.options);
  }).length;

  return (
    <div className="space-y-5">
      <section className="card p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-ink-900">Seçenek eksenleri</h2>
            <p className="mt-0.5 text-xs text-ink-500">
              En fazla 3 eksen tanımlayabilirsiniz (Renk, Beden, Gramaj gibi).
            </p>
          </div>
          {form.options.length < 3 ? (
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" className="btn-secondary btn-sm" onClick={addOption}>
                + Manuel Eksen Ekle
              </button>
              <button
                type="button"
                className="btn-ghost btn-sm text-brand-600 hover:text-brand-700 hover:bg-brand-50"
                onClick={() => setAttributePickerOpen(true)}
              >
                Katalog Özelliklerinden Ekle
              </button>
            </div>
          ) : null}
        </div>

        {form.options.length === 0 ? (
          <p className="rounded-lg bg-ink-50 px-4 py-3 text-sm text-ink-600">
            Eksen tanımlamazsanız ürün tek varyantlı olur; fiyat, barkod ve stok bilgileri Genel sekmesinden yönetilir.
          </p>
        ) : (
          <div className="space-y-4">
            {form.options.map((option, index) => (
              <div key={index} className="rounded-lg border border-ink-200 p-4">
                <div className="grid gap-3 sm:grid-cols-[1fr_160px_auto] sm:items-end">
                  <Field label="Eksen adı" htmlFor={`opt-name-${index}`} required>
                    <input
                      id={`opt-name-${index}`}
                      className="field"
                      placeholder="Örn: Renk, Beden, Gramaj"
                      value={option.name}
                      onChange={(event) => updateOption(index, { name: event.target.value })}
                    />
                  </Field>

                  <Field label="Gösterim" htmlFor={`opt-type-${index}`}>
                    <select
                      id={`opt-type-${index}`}
                      className="field"
                      value={option.displayType}
                      onChange={(event) => updateOption(index, { displayType: event.target.value })}
                    >
                      <option value="dropdown">Açılır liste</option>
                      <option value="swatch">Renk kutusu (Swatch)</option>
                      <option value="button">Düğme (Buton)</option>
                    </select>
                  </Field>

                  <button
                    type="button"
                    className="btn-ghost btn-sm mb-1 text-red-600 hover:bg-red-50"
                    onClick={() => removeOption(index)}
                  >
                    Kaldır
                  </button>
                </div>

                <Field
                  label="Değerler"
                  htmlFor={`opt-values-${index}`}
                  hint="Virgülle ayırın: Kırmızı, Mavi, Siyah veya 250g, 500g, 1000g"
                  className="mt-3"
                >
                  <input
                    id={`opt-values-${index}`}
                    className="field"
                    value={option.values.map((value) => value.value).join(', ')}
                    onChange={(event) =>
                      updateOption(index, {
                        values: event.target.value
                          .split(',')
                          .map((raw) => raw.trim())
                          .filter(Boolean)
                          .map((value, position) => {
                            const existing = option.values.find((item) => item.value === value);
                            return existing ?? { value, position };
                          }),
                      })
                    }
                  />
                </Field>

                {option.displayType === 'swatch' && option.values.length > 0 && (
                  <div className="mt-3 rounded-lg border border-ink-200 bg-ink-50/60 p-3.5">
                    <p className="mb-2 text-xs font-semibold text-ink-700">
                      Renk Kutusu (Swatch) Renk Kodları
                    </p>
                    <div className="flex flex-wrap gap-2.5">
                      {option.values.map((val, valIndex) => {
                        const currentColor = val.colorCode || suggestColor(val.value);
                        return (
                          <div
                            key={val.value}
                            className="flex items-center gap-2 rounded-lg border border-ink-200 bg-white px-2.5 py-1.5 text-xs shadow-sm"
                          >
                            <input
                              type="color"
                              value={
                                currentColor.startsWith('#') && currentColor.length === 7
                                  ? currentColor
                                  : '#000000'
                              }
                              onChange={(event) => {
                                const nextValues = [...option.values];
                                nextValues[valIndex] = {
                                  ...val,
                                  colorCode: event.target.value,
                                };
                                updateOption(index, { values: nextValues });
                              }}
                              className="h-6 w-6 cursor-pointer rounded-full border border-ink-300 p-0"
                              title={`${val.value} rengini seç`}
                            />
                            <span className="font-medium text-ink-800">{val.value}</span>
                            <input
                              type="text"
                              value={val.colorCode ?? ''}
                              placeholder={currentColor}
                              onChange={(event) => {
                                const nextValues = [...option.values];
                                nextValues[valIndex] = {
                                  ...val,
                                  colorCode: event.target.value,
                                };
                                updateOption(index, { values: nextValues });
                              }}
                              className="w-20 rounded border border-ink-200 px-1.5 py-0.5 font-mono text-xs uppercase text-ink-700 focus:border-ink-900 focus:outline-none"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Kombinasyon Eşitleme / Durum Çubuğu */}
            <div className="rounded-lg border border-ink-200 bg-ink-50/70 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                    {hasPlaceholder ? (
                      <span className="inline-flex items-center gap-1.5 text-amber-700">
                        <AlertCircle className="h-4 w-4 text-amber-600" />
                        Varyantlar henüz oluşturulmadı ({combinations.length} kombinasyon hazır)
                      </span>
                    ) : missingCombinations.length > 0 ? (
                      <span className="inline-flex items-center gap-1.5 text-blue-700">
                        <AlertCircle className="h-4 w-4 text-blue-600" />
                        Eksik Kombinasyonlar Var ({form.variants.length} / {combinations.length} varyant hazır)
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-emerald-700">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        Tüm varyant kombinasyonları senkronize ({form.variants.length} varyant)
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-ink-500">
                    Mevcut varyantların fiyat ve stokları korunur; yalnızca yeni kombinasyonlar eklenir.
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {missingCombinations.length > 0 && !hasPlaceholder && (
                    <button
                      type="button"
                      className="btn-primary btn-sm flex items-center gap-1.5"
                      onClick={() => onRegenerate('missingOnly')}
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Eksik Kombinasyonları Ekle ({missingCombinations.length})
                    </button>
                  )}
                  <button
                    type="button"
                    className={hasPlaceholder ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
                    onClick={() => onRegenerate('all')}
                  >
                    <RefreshCw className="h-3.5 w-3.5 mr-1" />
                    {hasPlaceholder ? `Varyantları Oluştur (${combinations.length})` : 'Tümünü Yeniden Eşitle'}
                  </button>
                </div>
              </div>
            </div>

            {/* Geçersiz Varyantlar Uyarısı */}
            {invalidVariantsCount > 0 && (
              <div className="flex items-center justify-between rounded-lg border border-amber-300 bg-amber-50 p-3.5">
                <div className="flex items-center gap-2 text-xs text-amber-900">
                  <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" />
                  <span>
                    Seçeneklerden kaldırılmış değerlere sahip <strong>{invalidVariantsCount} adet</strong> varyant bulunuyor. Kaydetmeden önce temizlenmelidir.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={cleanInvalidVariants}
                  className="btn-secondary btn-xs text-amber-900 border-amber-300 hover:bg-amber-100"
                >
                  Geçersizleri Temizle
                </button>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Varyant Listesi Tablosu */}
      <section className="card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between border-b border-ink-200 px-5 py-3.5 bg-ink-50/50">
          <div>
            <h2 className="text-sm font-semibold text-ink-900">
              Varyantlar ({form.variants.length})
            </h2>
            <p className="mt-0.5 text-xs text-ink-500">
              Her varyant için SKU, barkod, fiyat ve stok belirleyebilirsiniz.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowBulkBar(!showBulkBar)}
              className={`btn-sm flex items-center gap-1.5 transition ${
                showBulkBar ? 'btn-primary' : 'btn-secondary'
              }`}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              {showBulkBar ? 'Toplu İşlemleri Kapat' : 'Toplu Fiyat & Stok Belirle'}
            </button>
          </div>
        </div>

        {/* Toplu Değer Güncelleme Çubuğu */}
        {showBulkBar && (
          <div className="border-b border-ink-200 bg-brand-50/30 p-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-32">
                <label className="block text-xs font-semibold text-ink-700 mb-1">
                  Tümüne Fiyat
                </label>
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  className="field py-1.5 text-right"
                  placeholder="0.00"
                  value={bulkPrice}
                  onChange={(e) => setBulkPrice(e.target.value)}
                />
              </div>

              <div className="w-32">
                <label className="block text-xs font-semibold text-ink-700 mb-1">
                  İndirimli Fiyat
                </label>
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  className="field py-1.5 text-right"
                  placeholder="—"
                  value={bulkCompareAtPrice}
                  onChange={(e) => setBulkCompareAtPrice(e.target.value)}
                />
              </div>

              <div className="w-28">
                <label className="block text-xs font-semibold text-ink-700 mb-1">
                  Tümüne Stok
                </label>
                <input
                  type="number"
                  min={0}
                  className="field py-1.5 text-right"
                  placeholder="0"
                  value={bulkStock}
                  onChange={(e) => setBulkStock(e.target.value)}
                />
              </div>

              <button
                type="button"
                className="btn-primary btn-sm mb-0.5"
                onClick={applyBulkUpdates}
              >
                Tüm Varyantlara Uygula
              </button>

              <button
                type="button"
                className="btn-ghost btn-sm mb-0.5 text-ink-500"
                onClick={() => {
                  setBulkPrice('');
                  setBulkCompareAtPrice('');
                  setBulkStock('');
                  setShowBulkBar(false);
                }}
              >
                Kapat
              </button>
            </div>
          </div>
        )}

        <div className="scroll-x">
          <table className="w-full min-w-[1040px] text-sm">
            <thead>
              <tr className="border-b border-ink-200 bg-ink-50/70 text-left text-xs uppercase tracking-wide text-ink-500">
                <th className="px-3 py-2.5 font-semibold">ID</th>
                <th className="px-3 py-2.5 text-center font-semibold">Görsel</th>
                <th className="px-3 py-2.5 font-semibold">Varyant</th>
                <th className="px-3 py-2.5 font-semibold">SKU</th>
                <th className="px-3 py-2.5 font-semibold">Barkod</th>
                <th className="px-3 py-2.5 text-right font-semibold">Fiyat</th>
                <th className="px-3 py-2.5 text-right font-semibold">İndirimli Fiyat</th>
                <th className="px-3 py-2.5 text-right font-semibold">Alış Fiyatı</th>
                <th className="px-3 py-2.5 text-right font-semibold">Stok</th>
                <th className="px-3 py-2.5 text-right font-semibold">Ağırlık (g)</th>
                <th className="px-3 py-2.5 text-right font-semibold">Kritik eşik</th>
                <th className="px-3 py-2.5 text-center font-semibold">Durum</th>
                <th className="px-3 py-2.5 text-center font-semibold">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {form.variants.map((variant, index) => {
                const isPlaceholder = isPlaceholderVariant(variant);
                const isInvalid = form.options.length > 0 && !isPlaceholder && !isVariantValidForOptions(variant, form.options);

                return (
                  <tr key={index} className="hover:bg-ink-50/50 transition-colors">
                    <td className="px-3 py-2 whitespace-nowrap">
                      {variant.id ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-medium bg-ink-100 text-ink-700">
                          #{variant.id}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                          Yeni
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {variant.imageUrl ? (
                        <div className="group relative inline-block h-9 w-9 overflow-hidden rounded border border-ink-200 bg-white shadow-sm align-middle">
                          <img
                            src={variant.imageUrl}
                            alt={variant.title || 'Varyant görseli'}
                            className="h-full w-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => setPickerVariantIndex(index)}
                            className="absolute inset-0 flex items-center justify-center bg-black/50 text-white opacity-0 transition group-hover:opacity-100"
                            title="Görseli değiştir"
                          >
                            <Camera className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setPickerVariantIndex(index)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded border border-dashed border-ink-300 text-ink-400 hover:border-brand-500 hover:text-brand-600 hover:bg-brand-50/50 transition align-middle"
                          title="Varyanta görsel ata"
                        >
                          <ImagePlus className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {form.options.length === 0 ? (
                          <span className="font-medium text-ink-800">Standart / Tekil Ürün</span>
                        ) : isPlaceholder ? (
                          <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 border border-amber-200">
                            <AlertCircle className="h-3 w-3" />
                            Varyantlar Oluşturulmadı
                          </span>
                        ) : (
                          <>
                            <span className="font-medium text-ink-900">
                              {Object.values(variant.optionValues).join(' / ') || (variant.title ?? 'Varyant')}
                            </span>
                            {isInvalid && (
                              <span className="inline-flex items-center rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-600 border border-red-200">
                                Geçersiz Eksen / Değer
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        className="field py-1.5 font-mono text-xs w-28 sm:w-32"
                        placeholder="otomatik"
                        value={variant.sku ?? ''}
                        onChange={(event) => updateVariant(index, { sku: event.target.value })}
                        aria-label="SKU"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        className="field py-1.5 font-mono text-xs w-28 sm:w-32"
                        placeholder="Barkod"
                        value={variant.barcode ?? ''}
                        onChange={(event) => updateVariant(index, { barcode: event.target.value })}
                        aria-label="Barkod"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        step="0.01"
                        min={0}
                        className="field w-24 py-1.5 text-right font-medium"
                        value={variant.price}
                        onChange={(event) => updateVariant(index, { price: Number(event.target.value) })}
                        aria-label="Fiyat"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        step="0.01"
                        min={0}
                        className="field w-24 py-1.5 text-right"
                        placeholder="—"
                        value={variant.compareAtPrice ?? ''}
                        onChange={(event) =>
                          updateVariant(index, {
                            compareAtPrice: event.target.value ? Number(event.target.value) : null,
                          })
                        }
                        aria-label="İndirimli fiyat"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        step="0.01"
                        min={0}
                        className="field w-24 py-1.5 text-right"
                        placeholder="—"
                        value={variant.costPrice ?? ''}
                        onChange={(event) =>
                          updateVariant(index, {
                            costPrice: event.target.value ? Number(event.target.value) : null,
                          })
                        }
                        aria-label="Alış fiyatı"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min={0}
                        className="field w-20 py-1.5 text-right font-medium"
                        placeholder="0"
                        value={variant.initialStock ?? ''}
                        onChange={(event) =>
                          updateVariant(index, {
                            initialStock: event.target.value !== '' ? Number(event.target.value) : null,
                          })
                        }
                        aria-label="Stok"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        step="0.01"
                        min={0}
                        className="field w-20 py-1.5 text-right"
                        placeholder="0"
                        value={variant.weight ?? ''}
                        onChange={(event) =>
                          updateVariant(index, {
                            weight: event.target.value ? Number(event.target.value) : null,
                          })
                        }
                        aria-label="Ağırlık"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min={0}
                        className="field w-20 py-1.5 text-right"
                        value={variant.lowStockThreshold}
                        onChange={(event) =>
                          updateVariant(index, { lowStockThreshold: Number(event.target.value) })
                        }
                        aria-label="Kritik stok eşiği"
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => updateVariant(index, { isActive: !variant.isActive })}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold transition ${
                          variant.isActive
                            ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                            : 'bg-ink-100 text-ink-600 hover:bg-ink-200'
                        }`}
                        title={variant.isActive ? 'Pasife al' : 'Aktife al'}
                      >
                        {variant.isActive ? (
                          <>
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                            Aktif
                          </>
                        ) : (
                          <>
                            <XCircle className="h-3.5 w-3.5 text-ink-400" />
                            Pasif
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => removeVariant(index)}
                        disabled={form.options.length === 0}
                        className={`inline-flex items-center justify-center h-8 w-8 rounded-lg transition ${
                          form.options.length === 0
                            ? 'text-ink-300 cursor-not-allowed opacity-40'
                            : 'text-ink-400 hover:text-red-600 hover:bg-red-50'
                        }`}
                        title={
                          form.options.length === 0
                            ? 'Tekil ürünün ana varyantı silinemez'
                            : 'Bu varyantı sil'
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Eksik Kombinasyonlar Chip Listesi */}
        {missingCombinations.length > 0 && !hasPlaceholder && (
          <div className="border-t border-ink-200 bg-brand-50/30 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-brand-900">
                <AlertCircle className="h-4 w-4 text-brand-600" />
                Eklenmemiş Kombinasyonlar ({missingCombinations.length})
              </div>
              <button
                type="button"
                onClick={() => onRegenerate('missingOnly')}
                className="btn-primary btn-xs flex items-center gap-1"
              >
                <Plus className="h-3 w-3" />
                Tümünü Listeye Ekle
              </button>
            </div>
            <p className="text-xs text-ink-500 mb-2.5">
              Aşağıdaki kombinasyonlar seçenek eksenlerinde tanımlı ancak tabloda yok. Tek tek tıklayarak ekleyebilirsiniz:
            </p>
            <div className="flex flex-wrap gap-1.5">
              {missingCombinations.map((comb, cIdx) => (
                <button
                  key={cIdx}
                  type="button"
                  onClick={() => addSingleCombination(comb)}
                  className="inline-flex items-center gap-1 rounded-full border border-brand-300 bg-white px-2.5 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50 hover:border-brand-500 transition shadow-sm"
                  title="Bu kombinasyonu varyant tablosuna ekle"
                >
                  <Plus className="h-3 w-3" />
                  {Object.values(comb).join(' / ')}
                </button>
              ))}
            </div>
          </div>
        )}

        <p className="border-t border-ink-200 px-5 py-3 text-xs text-ink-500">
          Varyant stoklarında yapılan değişiklikler otomatik olarak stok defterine hareket kaydı olarak işlenir ve stok geçmişinde arşivlenir.
        </p>
      </section>

      {/* Varyant Görseli Seçim Modalı */}
      {pickerVariantIndex !== null && form.variants[pickerVariantIndex] && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="card w-full max-w-lg overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between border-b border-ink-200 px-5 py-3.5 bg-ink-50/50">
              <div>
                <h3 className="text-sm font-semibold text-ink-900">
                  Varyant Görseli Seç
                </h3>
                <p className="text-xs text-ink-500 mt-0.5">
                  {Object.values(form.variants[pickerVariantIndex].optionValues).join(' / ') || 'Varsayılan Varyant'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPickerVariantIndex(null)}
                className="rounded-md p-1.5 text-ink-400 hover:bg-ink-100 hover:text-ink-700 transition"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto p-5 space-y-5">
              {/* Mevcut ürün görselleri galerisi */}
              <div>
                <label className="text-xs font-semibold text-ink-700 block mb-2">
                  Ürün Görsellerinden Seç
                </label>
                {form.media.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-ink-200 p-6 text-center text-xs text-ink-500">
                    <ImageIcon className="mx-auto h-8 w-8 text-ink-300 mb-1" />
                    Henüz ürün görseli yüklenmemiş. Aşağıdan yükleyebilirsiniz.
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-2.5 max-h-48 overflow-y-auto p-1">
                    {form.media.map((media, mIdx) => {
                      const isSelected = form.variants[pickerVariantIndex]?.imageUrl === media.url;
                      return (
                        <button
                          key={mIdx}
                          type="button"
                          onClick={() => handleSelectMediaForVariant(media.url)}
                          className={`group relative aspect-square rounded-lg border-2 overflow-hidden bg-ink-50 transition ${
                            isSelected
                              ? 'border-brand-600 ring-2 ring-brand-500/20'
                              : 'border-ink-200 hover:border-ink-400'
                          }`}
                        >
                          <img
                            src={media.thumbnailUrl || media.url}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                          {isSelected && (
                            <div className="absolute inset-0 bg-brand-600/30 flex items-center justify-center">
                              <span className="rounded-full bg-brand-600 p-1 text-white shadow">
                                <Check className="h-3.5 w-3.5" />
                              </span>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Cihazdan Yeni Görsel Yükle */}
              <div className="pt-2 border-t border-ink-100">
                <label className="text-xs font-semibold text-ink-700 block mb-2">
                  Veya Cihazdan Yeni Görsel Yükle
                </label>
                <input
                  ref={variantFileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleVariantFileUpload(e.target.files)}
                />
                <button
                  type="button"
                  disabled={uploadingVariantImage}
                  onClick={() => variantFileInputRef.current?.click()}
                  className="btn-secondary btn-sm w-full flex items-center justify-center gap-2"
                >
                  {uploadingVariantImage ? (
                    <Spinner className="h-4 w-4" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  {uploadingVariantImage ? 'Yükleniyor...' : 'Bilgisayardan Görsel Seç'}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-ink-200 bg-ink-50/50 px-5 py-3">
              {form.variants[pickerVariantIndex]?.imageUrl ? (
                <button
                  type="button"
                  onClick={handleRemoveVariantImage}
                  className="btn-ghost btn-sm text-red-600 hover:bg-red-50 flex items-center gap-1.5"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Görseli Kaldır
                </button>
              ) : (
                <div />
              )}
              <button
                type="button"
                onClick={() => setPickerVariantIndex(null)}
                className="btn-secondary btn-sm"
              >
                Vazgeç
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Katalog Özellikleri Modal */}
      {attributePickerOpen && (
        <Modal
          open={attributePickerOpen}
          onClose={() => setAttributePickerOpen(false)}
          title="Katalog Özelliklerinden Eksen Ekle"
        >
          <div className="space-y-4">
            <p className="text-xs text-ink-500">
              Tanımlı merkezi özelliklerden (Renk, Beden vb.) birini seçerek tüm değerleriyle birlikte ürününüze tek tıkla ekleyin.
            </p>

            {attributesQuery.isLoading ? (
              <div className="py-8 flex justify-center"><Spinner /></div>
            ) : !attributesQuery.data || attributesQuery.data.length === 0 ? (
              <div className="py-6 text-center text-sm text-ink-500">
                Kayıtlı özellik bulunamadı. "Özellikler" sayfasından özellik ekleyebilirsiniz.
              </div>
            ) : (
              <div className="max-h-80 overflow-y-auto space-y-2 pr-1">
                {attributesQuery.data.map((attr: ProductAttributeDto) => {
                  const already = form.options.some((o) => o.name.toLowerCase() === attr.name.toLowerCase());
                  return (
                    <div
                      key={attr.id}
                      className={`flex items-center justify-between p-3 rounded-lg border transition ${
                        already
                          ? 'border-ink-200 bg-ink-50 opacity-60'
                          : 'border-ink-200 hover:border-indigo-300 hover:bg-indigo-50/30'
                      }`}
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          {attr.displayType === 'color' && <Palette className="h-4 w-4 text-indigo-500" />}
                          <span className="text-sm font-semibold text-ink-900">{attr.name}</span>
                          <span className="text-xs text-ink-400 font-mono">({attr.code})</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-1 mt-1.5">
                          {attr.values?.slice(0, 6).map((v: any) => (
                            <span
                              key={v.id}
                              className="inline-flex items-center gap-1 rounded bg-white px-1.5 py-0.5 text-xs border border-ink-200 text-ink-600"
                            >
                              {v.colorCode && (
                                <span
                                  className="h-2 w-2 rounded-full border border-ink-300"
                                  style={{ backgroundColor: v.colorCode }}
                                />
                              )}
                              {v.value}
                            </span>
                          ))}
                          {attr.values && attr.values.length > 6 && (
                            <span className="text-xs text-ink-400">+{attr.values.length - 6}</span>
                          )}
                        </div>
                      </div>

                      <button
                        type="button"
                        disabled={already}
                        onClick={() => addFromAttribute(attr)}
                        className={`btn-sm ${
                          already ? 'btn-ghost text-ink-400' : 'btn-primary'
                        }`}
                      >
                        {already ? 'Eklendi' : 'Seç & Ekle'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex justify-end pt-3 border-t border-ink-200">
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => setAttributePickerOpen(false)}
              >
                Kapat
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* --------------------------- Gorsel sekmesi --------------------------- */

function isVideoUrl(url?: string | null, kind?: string | null): boolean {
  if (kind === 'Video') return true;
  if (!url) return false;
  return /\.(mp4|webm|mov)(\?.*)?$/i.test(url);
}

function MediaTab({
  form,
  setForm,
}: {
  form: ProductUpsertDto;
  setForm: React.Dispatch<React.SetStateAction<ProductUpsertDto>>;
}) {
  const toast = useToast();
  const [uploading, setUploading] = useState(false);
  const [replacingIndex, setReplacingIndex] = useState<number | null>(null);
  const replaceInputRef = useRef<HTMLInputElement | null>(null);

  /**
   * Bu oturumda yuklenen dosyalarin depo yolu. Kullanici kaydetmeden
   * vazgecerse dosyayi depodan da silebilmek icin tutulur; daha once
   * kaydedilmis gorseller yalnizca urunden ayrilir, dosya silinmez.
   */
  const [uploadedPaths] = useState(() => new Map<string, string>());

  const limits = useQuery({
    queryKey: ['admin', 'file-limits'],
    queryFn: () => adminFilesApi.limits(),
    staleTime: 30 * 60_000,
  });

  const maxImgMb = limits.data?.maxImageSizeMb ?? 8;
  const maxVidMb = limits.data?.maxVideoSizeMb ?? 50;

  const validateFileSize = (file: File): boolean => {
    const isVid = file.type.startsWith('video/') || /\.(mp4|webm|mov)$/i.test(file.name);
    const limitMb = isVid ? maxVidMb : maxImgMb;
    if (file.size > limitMb * 1024 * 1024) {
      toast.error(
        isVid
          ? `"${file.name}" videosu çok büyük. Maksimum video boyutu ${limitMb} MB olabilir.`
          : `"${file.name}" görseli çok büyük. Maksimum görsel boyutu ${limitMb} MB olabilir.`,
      );
      return false;
    }
    return true;
  };

  const upload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const validFiles = Array.from(files).filter(validateFileSize);
    if (validFiles.length === 0) return;

    setUploading(true);
    try {
      const results = await adminFilesApi.uploadImages(validFiles, 'products');
      const uploaded = results.filter((result) => result.success && result.url);
      const failed = results.filter((result) => !result.success);

      if (failed.length > 0 && failed[0].errorMessage) {
        toast.error(failed[0].errorMessage);
      }

      uploaded.forEach((result) => {
        if (result.url && result.storagePath) {
          uploadedPaths.set(result.url, result.storagePath);
        }
      });

      if (uploaded.length > 0) {
        setForm((current) => {
          const hasAnyPrimary = current.media.some((m) => m.isPrimary);
          return {
            ...current,
            media: [
              ...current.media,
              ...uploaded.map((result, index) => {
                const isVideo =
                  result.contentType?.startsWith('video/') || isVideoUrl(result.url);
                return {
                  kind: (isVideo ? 'Video' : 'Image') as 'Image' | 'Video',
                  url: result.url!,
                  thumbnailUrl: result.thumbnailUrl ?? null,
                  altText: result.fileName ?? null,
                  position: current.media.length + index,
                  isPrimary: !hasAnyPrimary && index === 0 && !isVideo,
                };
              }),
            ],
          };
        });

        toast.success(`${uploaded.length} medya dosyası yüklendi.`);
      }
    } catch (error) {
      toast.fromError(error, 'Medya dosyası yüklenemedi.');
    } finally {
      setUploading(false);
    }
  };

  const onTriggerReplace = (index: number) => {
    setReplacingIndex(index);
    if (replaceInputRef.current) {
      replaceInputRef.current.value = '';
      replaceInputRef.current.click();
    }
  };

  const handleReplaceFile = async (files: FileList | null) => {
    if (!files || files.length === 0 || replacingIndex === null) return;
    const file = files[0];
    if (!validateFileSize(file)) {
      setReplacingIndex(null);
      return;
    }

    setUploading(true);
    try {
      const isVid = file.type.startsWith('video/') || /\.(mp4|webm|mov)$/i.test(file.name);
      const result = isVid
        ? await adminFilesApi.uploadVideo(file, 'products')
        : await adminFilesApi.uploadImage(file, 'products');
      if (!result.success || !result.url) {
        toast.error(result.errorMessage ?? 'Dosya yüklenemedi.');
        return;
      }

      if (result.storagePath) {
        uploadedPaths.set(result.url, result.storagePath);
      }

      const oldItem = form.media[replacingIndex];
      if (oldItem?.url && uploadedPaths.has(oldItem.url)) {
        const oldPath = uploadedPaths.get(oldItem.url);
        if (oldPath) {
          void adminFilesApi.remove(oldPath);
          uploadedPaths.delete(oldItem.url);
        }
      }

      const newIsVideo = result.contentType?.startsWith('video/') || isVideoUrl(result.url);

      setForm((current) => ({
        ...current,
        media: current.media.map((media, i) =>
          i === replacingIndex
            ? {
                ...media,
                kind: newIsVideo ? 'Video' : 'Image',
                url: result.url!,
                thumbnailUrl: result.thumbnailUrl ?? null,
                altText: media.altText || (result.fileName ?? null),
              }
            : media,
        ),
      }));

      toast.success(newIsVideo ? 'Video başarıyla güncellendi.' : 'Görsel başarıyla güncellendi.');
    } catch (error) {
      toast.fromError(error, 'Medya değiştirilemedi.');
    } finally {
      setUploading(false);
      setReplacingIndex(null);
    }
  };

  const moveMedia = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= form.media.length) return;
    setForm((current) => {
      const nextMedia = [...current.media];
      const [moved] = nextMedia.splice(fromIndex, 1);
      nextMedia.splice(toIndex, 0, moved);
      return {
        ...current,
        media: nextMedia.map((m, idx) => ({ ...m, position: idx })),
      };
    });
  };

  const removeMedia = async (index: number) => {
    const item = form.media[index];
    const wasPrimary = item.isPrimary;

    setForm((current) => {
      const media = current.media.filter((_, i) => i !== index);
      // Kapak silindiyse ilk gorsel kapak olur.
      if (wasPrimary && media.length > 0) {
        const firstImgIdx = media.findIndex((m) => m.kind !== 'Video');
        const targetIdx = firstImgIdx >= 0 ? firstImgIdx : 0;
        media[targetIdx] = { ...media[targetIdx], isPrimary: true };
      }
      return {
        ...current,
        media: media.map((m, idx) => ({ ...m, position: idx })),
      };
    });

    const storagePath = uploadedPaths.get(item.url);

    if (storagePath) {
      try {
        await adminFilesApi.remove(storagePath);
        uploadedPaths.delete(item.url);
      } catch {
        /* Dosya zaten yoksa sorun degil. */
      }
    }
  };

  return (
    <section className="card p-5">
      <input
        type="file"
        ref={replaceInputRef}
        accept="image/*,video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
        className="hidden"
        onChange={(event) => void handleReplaceFile(event.target.files)}
      />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-ink-900">Ürün Görselleri & Videoları</h2>
          <p className="mt-0.5 text-xs text-ink-500">
            Görsel: En fazla {maxImgMb} MB (Otomatik WebP) · Video: En fazla {maxVidMb} MB (.mp4, .webm, .mov)
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label className="btn-secondary btn-sm cursor-pointer">
            {uploading ? <Spinner className="h-4 w-4" /> : null}
            📷 Görsel yükle
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(event) => {
                void upload(event.target.files);
                event.target.value = '';
              }}
            />
          </label>

          <label className="btn-secondary btn-sm cursor-pointer border-brand-200 bg-brand-50/60 text-brand-800 hover:bg-brand-100">
            {uploading ? <Spinner className="h-4 w-4" /> : null}
            🎬 Video yükle (Maks {maxVidMb} MB)
            <input
              type="file"
              accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
              multiple
              className="hidden"
              onChange={(event) => {
                void upload(event.target.files);
                event.target.value = '';
              }}
            />
          </label>
        </div>
      </div>

      {form.media.length === 0 ? (
        <p className="rounded-lg border border-dashed border-ink-200 px-6 py-12 text-center text-sm text-ink-500">
          Henüz görsel veya video eklenmedi. Ürününüz için fotoğraf veya tanıtım videosu (maks. {maxVidMb} MB) yükleyebilirsiniz.
        </p>
      ) : (
        <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
          {form.media.map((item, index) => {
            const isVideo = isVideoUrl(item.url, item.kind);
            return (
              <li
                key={`${item.url}-${index}`}
                className="group relative flex flex-col rounded-xl border border-ink-200 bg-white p-2 shadow-xs transition hover:border-ink-300"
              >
                <div className="relative aspect-square overflow-hidden rounded-lg bg-ink-900">
                  {isVideo ? (
                    <video
                      src={item.url}
                      controls
                      muted
                      playsInline
                      preload="metadata"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <img
                      src={item.thumbnailUrl ?? item.url}
                      alt={item.altText ?? ''}
                      className="h-full w-full object-cover bg-ink-50"
                    />
                  )}

                  {item.isPrimary ? (
                    <span className="absolute left-1.5 top-1.5 badge bg-ink-900 text-white shadow-xs">
                      Kapak
                    </span>
                  ) : isVideo ? (
                    <span className="absolute left-1.5 top-1.5 badge bg-brand-600 text-white shadow-xs">
                      🎬 Video
                    </span>
                  ) : null}

                  <span className="absolute right-1.5 top-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                    #{index + 1}
                  </span>

                  {/* Sıralama Taşıma Butonları */}
                  <div className="absolute inset-x-0 bottom-1.5 flex justify-between px-1.5 opacity-90 transition group-hover:opacity-100 pointer-events-none">
                    <button
                      type="button"
                      disabled={index === 0}
                      onClick={() => moveMedia(index, index - 1)}
                      title="Sola / Öne taşı"
                      className="pointer-events-auto flex h-6 w-6 items-center justify-center rounded-full bg-black/75 text-white shadow-xs hover:bg-black disabled:invisible disabled:opacity-0 cursor-pointer"
                    >
                      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      disabled={index === form.media.length - 1}
                      onClick={() => moveMedia(index, index + 1)}
                      title="Sağa / Arkaya taşı"
                      className="pointer-events-auto flex h-6 w-6 items-center justify-center rounded-full bg-black/75 text-white shadow-xs hover:bg-black disabled:invisible disabled:opacity-0 cursor-pointer"
                    >
                      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* İşlemler: Kapak yap, Değiştir, Sil */}
                <div className="mt-2 flex flex-wrap items-center justify-between gap-1">
                  {!item.isPrimary ? (
                    <button
                      type="button"
                      className="btn-ghost btn-xs text-[11px] font-medium text-brand-700 hover:bg-brand-50"
                      onClick={() =>
                        setForm((current) => ({
                          ...current,
                          media: current.media.map((media, i) => ({
                            ...media,
                            isPrimary: i === index,
                          })),
                        }))
                      }
                    >
                      Kapak yap
                    </button>
                  ) : (
                    <span className="text-[11px] font-semibold text-brand-700 px-1">Ana Medya</span>
                  )}

                  <div className="flex items-center gap-0.5 ml-auto">
                    <button
                      type="button"
                      className="btn-ghost btn-xs text-[11px] text-ink-700 hover:text-ink-900"
                      onClick={() => onTriggerReplace(index)}
                      title="Bu dosyayı farklı bir dosyayla değiştir"
                    >
                      Değiştir
                    </button>
                    <button
                      type="button"
                      className="btn-ghost btn-xs text-[11px] text-red-600 hover:bg-red-50"
                      onClick={() => void removeMedia(index)}
                      title="Medyayı kaldır"
                    >
                      Sil
                    </button>
                  </div>
                </div>

                <input
                  className="field mt-1.5 py-1 text-xs"
                  placeholder={isVideo ? 'Video başlığı / açıklama' : 'Alternatif metin (SEO)'}
                  value={item.altText ?? ''}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      media: current.media.map((media, i) =>
                        i === index ? { ...media, altText: event.target.value } : media,
                      ),
                    }))
                  }
                  aria-label="Alternatif metin"
                />
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/* --------------------------- Ozellik sekmesi --------------------------- */

export function MetafieldsTab({
  form,
  setForm,
}: {
  form: ProductUpsertDto;
  setForm: React.Dispatch<React.SetStateAction<ProductUpsertDto>>;
}) {
  const add = () =>
    setForm((current) => ({
      ...current,
      metafields: [
        ...current.metafields,
        {
          namespace: 'custom',
          key: '',
          value: '',
          valueType: 'String',
          label: '',
          isPublic: true,
          showOnStorefront: true,
          isFilterable: false,
          position: current.metafields.length,
        },
      ],
    }));

  return (
    <section className="card p-5">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-ink-900">Ürün özellikleri</h2>
          <p className="mt-0.5 text-xs text-ink-500">
            Vitrinde özellik tablosunda gösterilir; filtrelenebilir olarak işaretlenenler filtre
            panelinde çıkar.
          </p>
        </div>
        <button type="button" className="btn-secondary btn-sm" onClick={add}>
          Özellik ekle
        </button>
      </div>

      {form.metafields.length === 0 ? (
        <p className="rounded-lg border border-dashed border-ink-200 px-6 py-10 text-center text-sm text-ink-500">
          Henüz özellik tanımlanmadı.
        </p>
      ) : (
        <ul className="space-y-3">
          {form.metafields.map((field, index) => (
            <li key={index} className="grid gap-3 rounded-lg border border-ink-200 p-3 sm:grid-cols-[1fr_1fr_auto]">
              <Field label="Etiket" htmlFor={`mf-label-${index}`}>
                <input
                  id={`mf-label-${index}`}
                  className="field"
                  placeholder="Malzeme"
                  value={field.label ?? ''}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      metafields: current.metafields.map((item, i) =>
                        i === index
                          ? {
                              ...item,
                              label: event.target.value,
                              key: item.key || slugifyKey(event.target.value),
                            }
                          : item,
                      ),
                    }))
                  }
                />
              </Field>

              <Field label="Değer" htmlFor={`mf-value-${index}`}>
                <input
                  id={`mf-value-${index}`}
                  className="field"
                  placeholder="%100 pamuk"
                  value={field.value ?? ''}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      metafields: current.metafields.map((item, i) =>
                        i === index ? { ...item, value: event.target.value } : item,
                      ),
                    }))
                  }
                />
              </Field>

              <div className="flex flex-col justify-end gap-2 pb-1">
                <label className="flex items-center gap-1.5 text-xs text-ink-600">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 rounded border-ink-300 text-brand-600"
                    checked={field.isFilterable}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        metafields: current.metafields.map((item, i) =>
                          i === index ? { ...item, isFilterable: event.target.checked } : item,
                        ),
                      }))
                    }
                  />
                  Filtrelenebilir
                </label>
                <button
                  type="button"
                  className="btn-ghost btn-sm text-red-600"
                  onClick={() =>
                    setForm((current) => ({
                      ...current,
                      metafields: current.metafields.filter((_, i) => i !== index),
                    }))
                  }
                >
                  Kaldır
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function slugifyKey(value: string): string {
  return value
    .toLocaleLowerCase('tr-TR')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
